/**
 * Job Priorities — replaces the old task x complexity Skill Matrix.
 *
 * Each operator carries two ORDERED lists: which task types they do, and which
 * job natures. Ticking an item means "can do it"; its position means "prefers
 * it". When an operator asks for their next job, the cutoff decides first and
 * these lists break the tie.
 *
 * Priorities are usually set on a GROUP and shared by its members. A per-user
 * list overrides the group for the odd person who is a special case.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { GripVertical, Users, Plus, Trash2, Save, X, Search } from 'lucide-react';

// ── One reorderable, tickable list ──────────────────────────────────────────
function PriorityList({ title, hint, options, value, onChange }) {
  const [dragIndex, setDragIndex] = useState(null);

  // Ticked items keep their saved order and sit on top; the rest follow
  // alphabetically so they are easy to find and tick.
  const rows = useMemo(() => {
    const chosen = value.filter((v) => options.includes(v));
    const rest = options.filter((o) => !chosen.includes(o)).sort();
    return [...chosen, ...rest];
  }, [options, value]);

  const isOn = (name) => value.includes(name);

  const toggle = (name) => {
    onChange(isOn(name) ? value.filter((v) => v !== name) : [...value, name]);
  };

  // Dropping onto another row moves the dragged item to that position. Only
  // ticked rows take part — an unticked item has no priority to order.
  const handleDrop = (targetName) => {
    if (dragIndex === null) return;
    const from = value.indexOf(dragIndex);
    const to = value.indexOf(targetName);
    setDragIndex(null);
    if (from === -1 || to === -1 || from === to) return;
    const next = [...value];
    next.splice(to, 0, next.splice(from, 1)[0]);
    onChange(next);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <p className="text-xs text-gray-500 mt-0.5">{hint}</p>
      </div>
      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
        {rows.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">Nothing to choose from.</p>
        )}
        {rows.map((name) => {
          const on = isOn(name);
          const rank = value.indexOf(name) + 1;
          return (
            <div
              key={name}
              draggable={on}
              onDragStart={() => on && setDragIndex(name)}
              onDragOver={(e) => on && e.preventDefault()}
              onDrop={() => on && handleDrop(name)}
              className={`flex items-center gap-3 px-3 py-2 ${
                on ? 'bg-white' : 'bg-gray-50/60'
              } ${dragIndex === name ? 'opacity-50' : ''}`}
            >
              <span
                className={`w-7 h-7 shrink-0 rounded flex items-center justify-center text-xs font-semibold ${
                  on ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-300'
                }`}
              >
                {on ? rank : '—'}
              </span>
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(name)}
                className="w-4 h-4 accent-blue-600 cursor-pointer shrink-0"
              />
              <span className={`flex-1 text-sm truncate ${on ? 'text-gray-800' : 'text-gray-400'}`}>
                {name}
              </span>
              {on && (
                <GripVertical
                  className="w-4 h-4 text-gray-300 cursor-grab shrink-0"
                  title="Drag to reorder"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function JobPriorities() {
  const { token } = useAuth();
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [tab, setTab] = useState('users');      // 'users' | 'groups'
  const [options, setOptions] = useState({ tasks: [], natures: [] });
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Editing buffers. `editing` is the user_id or group id being edited.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ task_priorities: [], nature_priorities: [] });
  const [saving, setSaving] = useState(false);

  const [newGroupName, setNewGroupName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [opt, us, gr] = await Promise.all([
        api.get('/admin/job-priorities/options', authHeader),
        api.get('/admin/job-priorities', authHeader),
        api.get('/admin/skill-groups', authHeader),
      ]);
      setOptions({ tasks: opt.data?.tasks || [], natures: opt.data?.natures || [] });
      setUsers(us.data || []);
      setGroups(gr.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load job priorities');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  // Seeded from the user's OWN lists, not the effective ones. Seeding from the
  // effective list would pre-fill a group member's editor with the group's
  // values, so merely opening and saving would copy them onto the user and cut
  // them off from future group changes.
  const startEditUser = (u) => {
    setEditing(u.user_id);
    setDraft({
      task_priorities: [...(u.own_task_priorities || [])],
      nature_priorities: [...(u.own_nature_priorities || [])],
    });
  };

  const startEditGroup = (g) => {
    setEditing(g.id);
    setDraft({
      task_priorities: [...(g.task_priorities || [])],
      nature_priorities: [...(g.nature_priorities || [])],
      user_ids: [...(g.user_ids || [])],
      name: g.name,
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft({ task_priorities: [], nature_priorities: [] }); };

  const saveUser = async (userId) => {
    setSaving(true);
    try {
      await api.put(`/admin/job-priorities/${userId}`, {
        task_priorities: draft.task_priorities,
        nature_priorities: draft.nature_priorities,
      }, authHeader);
      await load();
      cancelEdit();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveGroup = async (groupId) => {
    setSaving(true);
    try {
      await api.put(`/admin/skill-groups/${groupId}`, {
        name: draft.name,
        task_priorities: draft.task_priorities,
        nature_priorities: draft.nature_priorities,
        user_ids: draft.user_ids || [],
      }, authHeader);
      await load();
      cancelEdit();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await api.post('/admin/skill-groups', { name }, authHeader);
      setNewGroupName('');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create the group');
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? Members fall back to their own priorities.')) return;
    try {
      await api.delete(`/admin/skill-groups/${groupId}`, authHeader);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Delete failed');
    }
  };

  const filteredUsers = users.filter((u) =>
    !search.trim() || u.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const editorPanels = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <PriorityList
        title="Task priority"
        hint="Which steps this operator works, best first."
        options={options.tasks}
        value={draft.task_priorities}
        onChange={(v) => setDraft((d) => ({ ...d, task_priorities: v }))}
      />
      <PriorityList
        title="Job nature priority"
        hint="Which kinds of job, best first. Leave empty to accept any."
        options={options.natures}
        value={draft.nature_priorities}
        onChange={(v) => setDraft((d) => ({ ...d, nature_priorities: v }))}
      />
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-800">Job Priorities</h2>
      <p className="text-sm text-gray-500 mt-1 max-w-3xl">
        Sets the order work is offered in when an operator asks for their next job.
        The client cutoff always decides first — these lists break the tie. Only
        users with <strong>Auto job assign</strong> switched on in their profile can
        pull their own jobs; everyone else is assigned work by their TL or PM as before.
      </p>

      {error && (
        <div className="mt-4 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2 mt-4 border-b border-gray-200">
        {[['users', 'Per user'], ['groups', 'Groups']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => { setTab(id); cancelEdit(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 animate-pulse">Loading…</div>
      ) : tab === 'users' ? (
        <div className="mt-4">
          <div className="relative max-w-xs mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search operator…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {filteredUsers.length === 0 && (
              <p className="px-4 py-8 text-sm text-gray-400 text-center">No operators match.</p>
            )}
            {filteredUsers.map((u) => (
              <div key={u.user_id} className="px-4 py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium text-gray-800">{u.name}</span>
                  <span className="text-xs text-gray-500">{u.role}</span>
                  {u.auto_job_assign_enabled ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                      Auto assign on
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      Assigned by TL/PM
                    </span>
                  )}
                  {u.group_name && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      Group: {u.group_name}
                    </span>
                  )}
                  <button
                    onClick={() => (editing === u.user_id ? cancelEdit() : startEditUser(u))}
                    className="ml-auto text-sm text-blue-700 hover:underline"
                  >{editing === u.user_id ? 'Close' : 'Edit priorities'}</button>
                </div>

                {editing !== u.user_id && (
                  <div className="text-xs text-gray-500 mt-1">
                    {(u.task_priorities || []).length
                      ? `${u.task_priorities.join(' → ')}${
                          (u.nature_priorities || []).length ? `  ·  ${u.nature_priorities.join(' → ')}` : ''
                        }`
                      : 'No priorities set'}
                    {u.source?.tasks === 'group' && ' (from group)'}
                  </div>
                )}

                {editing === u.user_id && (
                  <>
                    {u.group_name && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3">
                        This user is in group <strong>{u.group_name}</strong>. Saving here creates a
                        per-user override that wins over the group. Clear both lists to go back to the group.
                      </p>
                    )}
                    {editorPanels}
                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={cancelEdit} className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                      <button
                        onClick={() => saveUser(u.user_id)}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                      ><Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex gap-2 mb-4">
            <input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createGroup()}
              placeholder="New group name…"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={createGroup}
              disabled={!newGroupName.trim()}
              className="px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            ><Plus className="w-4 h-4" /> Add group</button>
          </div>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {groups.length === 0 && (
              <p className="px-4 py-8 text-sm text-gray-400 text-center">
                No groups yet. A group lets several operators share one set of priorities.
              </p>
            )}
            {groups.map((g) => (
              <div key={g.id} className="px-4 py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-800">{g.name}</span>
                  <span className="text-xs text-gray-500">
                    {g.members.length} member{g.members.length === 1 ? '' : 's'}
                  </span>
                  <button
                    onClick={() => (editing === g.id ? cancelEdit() : startEditGroup(g))}
                    className="ml-auto text-sm text-blue-700 hover:underline"
                  >{editing === g.id ? 'Close' : 'Edit'}</button>
                  <button
                    onClick={() => deleteGroup(g.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Delete group"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>

                {editing !== g.id && (
                  <div className="text-xs text-gray-500 mt-1">
                    {(g.task_priorities || []).length
                      ? g.task_priorities.join(' → ')
                      : 'No task priorities set'}
                    {g.members.length > 0 && `  ·  ${g.members.map((m) => m.name).join(', ')}`}
                  </div>
                )}

                {editing === g.id && (
                  <>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Group name</label>
                      <input
                        value={draft.name || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {editorPanels}

                    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800">Members</h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          A user belongs to one group only — adding them here removes them from any other.
                        </p>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {users.map((u) => (
                          <label key={u.user_id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={(draft.user_ids || []).includes(u.user_id)}
                              onChange={(e) => setDraft((d) => ({
                                ...d,
                                user_ids: e.target.checked
                                  ? [...(d.user_ids || []), u.user_id]
                                  : (d.user_ids || []).filter((x) => x !== u.user_id),
                              }))}
                              className="w-4 h-4 accent-blue-600 cursor-pointer"
                            />
                            <span className="truncate">{u.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={cancelEdit} className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 inline-flex items-center gap-1.5">
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                      <button
                        onClick={() => saveGroup(g.id)}
                        disabled={saving}
                        className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                      ><Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save group'}</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
